/* T119 — Project Status Distribution card for the Dashboard.
 *
 * Sprint 5 layered addition. Sits at the top of /dashboard, between
 * the page header and the recent-projects table. Pulls /api/v1/projects
 * with each status filter to derive the counters; renders the
 * design-aligned 3-zone dist-card:
 *
 *   LEFT  — title + big total + "X of Y audits complete · Z flagged"
 *   MID   — stacked status bar + dot legend
 *   RIGHT — donut (in-pipeline + complete) + type list
 *
 * Mirrors `New UI Changes/dashboard.html` `<section class="dist-card">`.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { listProjects } from '../../services/api';
import { useCountUp } from '../../hooks/useCountUp';

interface Counts {
  in_progress: number;
  flagged: number;
  complete: number;
  failed: number;
}

const ZERO: Counts = { in_progress: 0, flagged: 0, complete: 0, failed: 0 };

export function ProjectStatusDistribution(): JSX.Element | null {
  const navigate = useNavigate();
  const [counts, setCounts] = useState<Counts>(ZERO);
  const [loaded, setLoaded] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    void load();

    async function load(): Promise<void> {
      try {
        const [ip, fl, co, fa] = await Promise.all([
          listProjects({ status: 'in_progress', limit: 1 }),
          listProjects({ status: 'flagged', limit: 1 }),
          listProjects({ status: 'complete', limit: 1 }),
          listProjects({ status: 'failed', limit: 1 }),
        ]);
        setCounts({
          in_progress: ip.total,
          flagged: fl.total,
          complete: co.total,
          failed: fa.total,
        });
        setLoaded(true);
      } catch {
        setHidden(true);
      }
    }
  }, []);

  const total =
    counts.in_progress + counts.flagged + counts.complete + counts.failed;
  // Count-up for the hero total (displayed only — arc math still uses `total`).
  const shownTotal = useCountUp(total, 1200);

  if (hidden) return null;

  if (loaded && total === 0) {
    return (
      <div
        style={{
          padding: 'var(--s5)',
          background: 'var(--surface)',
          border: '1px solid var(--lines)',
          borderRadius: 'var(--r-md)',
          margin: 'var(--s4) 0',
          textAlign: 'center',
        }}
      >
        <p style={{ marginBottom: 'var(--s3)', color: 'var(--muted)' }}>
          No Projects yet. Start your first Project to use the 3-stage audit
          pipeline (DD → SPA → Risk Assessment).
        </p>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => navigate('/projects/new')}
        >
          + New Project
        </button>
      </div>
    );
  }

  const inPipeline = counts.in_progress + counts.flagged;
  // Status bar percentages (4 segments, failed only renders if > 0)
  const ipPct = total ? (counts.in_progress / total) * 100 : 0;
  const flPct = total ? (counts.flagged / total) * 100 : 0;
  const coPct = total ? (counts.complete / total) * 100 : 0;
  const faPct = total ? (counts.failed / total) * 100 : 0;

  // Donut percentages — design uses 2 slices (in-pipeline + complete).
  // Failed becomes a third slice when present so the ring still adds
  // to 100% rather than leaving an unexplained gap.
  const donutPipelinePct = total ? (inPipeline / total) * 100 : 0;
  const donutCompletePct = total ? (counts.complete / total) * 100 : 0;
  const donutFailedPct = total ? (counts.failed / total) * 100 : 0;

  return (
    <section className="dist-card" aria-label="Project status distribution">
      {/* LEFT — title + big total + rate line */}
      <div className="dist-summary">
        <h2 className="dist-title">Project Status</h2>
        <div className="dist-total">
          <span className="dist-total-num">{Math.round(shownTotal)}</span>
          <span className="dist-total-lbl">Projects</span>
        </div>
        <p className="dist-rate">
          {counts.complete} of {total} audits complete · {counts.flagged} flagged
        </p>
      </div>

      {/* MIDDLE — stacked bar + dot legend */}
      <div className="dist-bars">
        <div
          className="dist-bar"
          role="img"
          aria-label={`In Progress ${counts.in_progress}, Flagged ${counts.flagged}, Completed ${counts.complete}${counts.failed ? `, Failed ${counts.failed}` : ''}`}
        >
          {ipPct > 0 && <span className="c-accent" style={{ width: `${ipPct}%` }} data-tip={`In Progress: ${counts.in_progress}`} />}
          {flPct > 0 && <span className="c-watch" style={{ width: `${flPct}%` }} data-tip={`Flagged: ${counts.flagged}`} />}
          {coPct > 0 && <span className="c-pass" style={{ width: `${coPct}%` }} data-tip={`Completed: ${counts.complete}`} />}
          {faPct > 0 && <span className="c-fail" style={{ width: `${faPct}%` }} data-tip={`Failed: ${counts.failed}`} />}
        </div>
        <div className="dist-legend">
          <span className="dist-leg"><span className="dist-dot c-accent" />{counts.in_progress} In Progress</span>
          <span className="dist-leg"><span className="dist-dot c-watch" />{counts.flagged} Flagged</span>
          <span className="dist-leg"><span className="dist-dot c-pass" />{counts.complete} Completed</span>
          {counts.failed > 0 && (
            <span className="dist-leg"><span className="dist-dot c-fail" />{counts.failed} Failed</span>
          )}
        </div>
      </div>

      {/* RIGHT — donut + type list */}
      <div className="dist-types">
        <div className="dist-donut">
          <svg width="92" height="92" viewBox="0 0 36 36" aria-hidden="true">
            <circle
              className="stk-track"
              cx="18" cy="18" r="15.9155"
              fill="none" strokeWidth="3.5"
            />
            {donutPipelinePct > 0 && (
              <circle
                className="stk-accent"
                cx="18" cy="18" r="15.9155"
                fill="none" strokeWidth="3.5"
                strokeDasharray={`${donutPipelinePct} ${100 - donutPipelinePct}`}
                strokeDashoffset="0"
              />
            )}
            {donutCompletePct > 0 && (
              <circle
                className="stk-pass"
                cx="18" cy="18" r="15.9155"
                fill="none" strokeWidth="3.5"
                strokeDasharray={`${donutCompletePct} ${100 - donutCompletePct}`}
                strokeDashoffset={`-${donutPipelinePct}`}
              />
            )}
            {donutFailedPct > 0 && (
              <circle
                className="stk-fail"
                cx="18" cy="18" r="15.9155"
                fill="none" strokeWidth="3.5"
                strokeDasharray={`${donutFailedPct} ${100 - donutFailedPct}`}
                strokeDashoffset={`-${donutPipelinePct + donutCompletePct}`}
              />
            )}
          </svg>
          <div className="dist-donut-c">
            <b>{Math.round(shownTotal)}</b>
            <span>Total</span>
          </div>
        </div>
        <div className="dist-type-list">
          <div className="dist-type">
            <span className="dist-type-sw c-accent" />
            <div className="dist-type-meta">
              <span className="dist-type-lbl">In pipeline (DD / SPA)</span>
              <span className="dist-type-num">{inPipeline}</span>
            </div>
          </div>
          <div className="dist-type">
            <span className="dist-type-sw c-pass" />
            <div className="dist-type-meta">
              <span className="dist-type-lbl">Completed</span>
              <span className="dist-type-num">{counts.complete}</span>
            </div>
          </div>
          {counts.failed > 0 && (
            <div className="dist-type">
              <span className="dist-type-sw c-fail" />
              <div className="dist-type-meta">
                <span className="dist-type-lbl">Failed</span>
                <span className="dist-type-num">{counts.failed}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="dist-actions">
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate('/projects')}
        >
          View all →
        </button>
      </div>
    </section>
  );
}
